import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DiaDaQualidade } from '../../auditoria';
import { Ajuda } from '../../design/Ajuda';
import { ErroDeArea } from '../../design/Estados';
import { diaCurto, lerRelatorioDeErros } from '../../giro';
import type {
  BloqueioDoRelatorio,
  ItemDoRelatorio,
  RelatorioDeErrosDoGiro,
  SituacaoNoRelatorio,
} from '../../giro';
import '../paineis/visao-geral.css';
import { SubnavDoGiro } from './SubnavDoGiro';

/**
 * RELATÓRIO DE ERROS DO GIRO (dono + Carlos, 19/10/2026) — "quanto cada balconista tá errando, o
 * que tá errando, o que pediu pra repor, o que ele colocou na prateleira, o que pegou no estoque,
 * a foto da cesta… o motivo do bloqueio".
 *
 * O % é o MESMO da política de qualidade (falha de reposição confirmada ÷ mandados repor) e vem
 * pronto do servidor; a tela só filtra (datas e balconista no servidor; situação, setor e produto
 * aqui) e mostra. Só leitura: liberar/bloquear continua na tela Bloqueados.
 */

type Periodo = 'hoje' | 'ontem' | '7' | '30' | 'periodo';
const PERIODOS: readonly { v: Periodo; r: string }[] = [
  { v: 'hoje', r: 'Hoje' },
  { v: 'ontem', r: 'Ontem' },
  { v: '7', r: '7 dias' },
  { v: '30', r: '30 dias' },
  { v: 'periodo', r: 'Escolher datas' },
];
const hojeBr = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
const somar = (dia: string, n: number) =>
  new Date(Date.parse(`${dia}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const pct = (n: number) => `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const ehDia = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);

function intervalo(p: Periodo, de: string, ate: string): { de: string; ate: string } {
  const h = hojeBr();
  if (p === 'hoje') return { de: h, ate: h };
  if (p === 'ontem') return { de: somar(h, -1), ate: somar(h, -1) };
  if (p === '7') return { de: somar(h, -6), ate: h };
  if (p === '30') return { de: somar(h, -29), ate: h };
  const d = ehDia(de) ? de : h;
  return { de: d, ate: ehDia(ate) && ate >= d ? ate : d };
}

const SITUACAO: Record<SituacaoNoRelatorio, { rotulo: string; classe: string; ajuda: string }> = {
  FALHA_CONFIRMADA: {
    rotulo: 'Erro confirmado',
    classe: 'gr-st-div',
    ajuda: 'Disse que não tinha para repor e o estoque tinha. Conta no %.',
  },
  EM_ANALISE: {
    rotulo: 'Em análise',
    classe: 'gr-st-rep',
    ajuda: 'O Igor/Felipe ainda não fechou o caso. Não conta enquanto espera.',
  },
  SEM_FALHA: {
    rotulo: 'Estava certo',
    classe: 'gr-st-ok',
    ajuda: 'O caso fechou sem falha: o estoque não tinha mesmo.',
  },
  OUTRA_DIVERGENCIA: {
    rotulo: 'Outra divergência',
    classe: '',
    ajuda: 'Estoque inconsistente ou outro tipo: não pesa na reposição.',
  },
  REPOSTO: { rotulo: 'Repôs tudo', classe: 'gr-st-ok', ajuda: 'Repôs o que foi pedido.' },
  REPOSICAO_INCOMPLETA: {
    rotulo: 'Repôs menos',
    classe: 'gr-st-rep',
    ajuda: 'Repôs menos que o pedido, sem divergência aberta (ou ainda não terminou).',
  },
};
const ETAPA: Record<string, string> = {
  PRATELEIRA: 'Igor: prateleira',
  ESTOQUE: 'Igor: estoque',
  AJUSTE: 'Felipe: ajuste',
};
const TIPO: Record<string, string> = {
  FALTOU_NA_REPOSICAO: 'Faltou na reposição',
  ESTOQUE_INCONSISTENTE: 'Estoque inconsistente',
};
const ORIGEM: Record<string, string> = {
  PERCENTUAL: 'Automático (%)',
  CONTAGEM: 'Automático (contagem)',
  MANUAL: 'Manual',
  POR_ERRO: 'Antigo (por erro)',
};

const n = (v: number | null) => (v === null ? '—' : String(v));

interface LinhaDoResumo {
  readonly accountId: string;
  readonly nome: string;
  readonly d: DiaDaQualidade;
  readonly bloqueio: BloqueioDoRelatorio | null;
}

/** Planilha (abre no Excel): ponto e vírgula + BOM, que é o que o Excel brasileiro entende. */
function baixarCsv(nome: string, linhas: readonly (readonly (string | number | null)[])[]) {
  const cel = (v: string | number | null) => {
    const s = v === null ? '' : String(v);
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const texto = `\uFEFF${linhas.map((l) => l.map(cel).join(';')).join('\r\n')}`;
  const url = URL.createObjectURL(new Blob([texto], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
}

export function RelatorioDoGiro() {
  const [periodo, setPeriodo] = useState<Periodo>('ontem');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [pessoa, setPessoa] = useState('');
  const [situacao, setSituacao] = useState<'' | 'ERROS' | SituacaoNoRelatorio>('');
  const [setor, setSetor] = useState('');
  const [busca, setBusca] = useState('');
  const [diaSel, setDiaSel] = useState('');
  const [limite, setLimite] = useState(200);
  const [dados, setDados] = useState<RelatorioDeErrosDoGiro | null>(null);
  const [erro, setErro] = useState(false);
  const [foto, setFoto] = useState<{ id: string; titulo: string } | null>(null);
  const per = intervalo(periodo, de, ate);

  const carregar = useCallback(() => {
    setErro(false);
    setDados(null);
    void lerRelatorioDeErros(per.de, per.ate).then((r) =>
      r.ok ? setDados(r.dados) : setErro(true),
    );
  }, [per.de, per.ate]);
  useEffect(carregar, [carregar]);
  useEffect(() => setLimite(200), [pessoa, situacao, setor, busca, diaSel, per.de, per.ate]);

  const pessoas = useMemo(
    () =>
      [...(dados?.resumo.pessoas ?? [])]
        .map((p) => ({ id: p.accountId, nome: p.nome }))
        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [dados],
  );
  const setores = useMemo(
    () => [...new Set((dados?.itens ?? []).map((i) => i.setor))].sort(),
    [dados],
  );

  const resumo = useMemo((): LinhaDoResumo[] => {
    if (!dados) return [];
    const bl = (conta: string, dia: string) =>
      dados.bloqueios.find((b) => b.accountId === conta && b.diaGiro === dia) ?? null;
    return dados.resumo.pessoas
      .filter((p) => !pessoa || p.accountId === pessoa)
      .flatMap((p) =>
        p.dias
          .filter((d) => !diaSel || d.dia === diaSel)
          .map((d) => ({
            accountId: p.accountId,
            nome: p.nome,
            d,
            bloqueio: bl(p.accountId, d.dia),
          })),
      )
      .sort(
        (a, b) =>
          (a.d.dia < b.d.dia ? 1 : a.d.dia > b.d.dia ? -1 : 0) ||
          b.d.avaliacao.reposicao.taxa - a.d.avaliacao.reposicao.taxa ||
          b.d.avaliacao.reposicao.taxaMaxima - a.d.avaliacao.reposicao.taxaMaxima ||
          a.nome.localeCompare(b.nome, 'pt-BR'),
      );
  }, [dados, pessoa, diaSel]);

  const itens = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return (dados?.itens ?? []).filter(
      (i) =>
        (!pessoa || i.accountId === pessoa) &&
        (!diaSel || i.dia === diaSel) &&
        (!setor || i.setor === setor) &&
        (!situacao ||
          (situacao === 'ERROS'
            ? i.situacao === 'FALHA_CONFIRMADA' || i.situacao === 'EM_ANALISE'
            : i.situacao === situacao)) &&
        (!t || i.produto.toLowerCase().includes(t) || (i.codigo ?? '').includes(t)),
    );
  }, [dados, pessoa, diaSel, setor, situacao, busca]);

  const bloqueios = (dados?.bloqueios ?? []).filter(
    (b) => (!pessoa || b.accountId === pessoa) && (!diaSel || b.diaGiro === diaSel),
  );

  const tot = resumo.reduce(
    (t, l) => ({
      tarefas: t.tarefas + l.d.conta.tarefasReposicao,
      falhas: t.falhas + l.d.conta.falhasReposicao,
      pendentes: t.pendentes + l.d.conta.pendentesReposicao,
    }),
    { tarefas: 0, falhas: 0, pendentes: 0 },
  );
  const taxa = tot.tarefas > 0 ? Math.round((tot.falhas / tot.tarefas) * 1000) / 10 : 0;
  const taxaMax =
    tot.tarefas > 0 ? Math.round(((tot.falhas + tot.pendentes) / tot.tarefas) * 1000) / 10 : 0;
  const unid = itens.reduce(
    (t, i) => ({ pediu: t.pediu + (i.repor ?? 0), repos: t.repos + (i.reposto ?? 0) }),
    { pediu: 0, repos: 0 },
  );
  const limitePct = dados?.resumo.politica.limitePct ?? 30;

  const exportar = () =>
    baixarCsv(`giro-erros-${per.de}-a-${per.ate}.csv`, [
      [
        'Giro',
        'Balconista',
        'Setor',
        'Produto',
        'Código',
        'Prateleira (contou)',
        'Pediu repor',
        'Achou no estoque',
        'Repôs',
        'Situação',
        'Divergência',
        'Etapa',
        'Igor recontou',
        'Auditor',
        'Justificativa',
        'Foto prateleira',
        'Foto cesta',
      ],
      ...itens.map((i) => [
        diaCurto(i.dia),
        i.balconista,
        i.setor,
        i.produto,
        i.codigo,
        i.prateleira,
        i.repor,
        i.encontrado,
        i.reposto,
        SITUACAO[i.situacao].rotulo,
        i.divergencia ? (TIPO[i.divergencia.tipo] ?? i.divergencia.tipo) : null,
        i.divergencia ? (ETAPA[i.divergencia.etapa] ?? i.divergencia.etapa) : null,
        i.divergencia?.recontagem ?? null,
        i.divergencia?.auditor ?? null,
        i.divergencia?.observacao ?? null,
        i.fotoPrateleira ? 'sim' : 'não',
        i.fotoCesta ? 'sim' : 'não',
      ]),
    ]);

  return (
    <section className="cv gr">
      <SubnavDoGiro atual="giro-relatorio" />
      <div className="cv-corpo">
        <div className="cv-titulo cv-titulo-2l">
          <h1>Relatório de erros</h1>
          <span>
            Quanto cada balconista está errando na reposição do Giro e em quê: produto a produto, o
            que contou na prateleira, o que foi pedido repor, o que achou no estoque, o que repôs, a
            foto da prateleira e da cesta, o que o Igor/Felipe achou e o motivo de cada bloqueio.
          </span>
        </div>

        <div className="cv-cartao">
          <div className="vg-segmentos cv-abas" role="tablist" aria-label="Período">
            {PERIODOS.map((p) => (
              <button
                key={p.v}
                type="button"
                role="tab"
                aria-selected={periodo === p.v}
                className={`vg-seg${periodo === p.v ? ' vg-seg-ativo' : ''}`}
                onClick={() => {
                  setPeriodo(p.v);
                  setDiaSel('');
                }}
              >
                {p.r}
              </button>
            ))}
          </div>
          <div className="gr-capac-acoes">
            {periodo === 'periodo' && (
              <>
                <label>
                  <span className="gr-rotulo">DE</span>
                  <input
                    type="date"
                    className="gr-input"
                    value={de}
                    max={hojeBr()}
                    onChange={(e) => setDe(e.target.value)}
                  />
                </label>
                <label>
                  <span className="gr-rotulo">ATÉ</span>
                  <input
                    type="date"
                    className="gr-input"
                    value={ate}
                    max={hojeBr()}
                    onChange={(e) => setAte(e.target.value)}
                  />
                </label>
              </>
            )}
            <label>
              <span className="gr-rotulo">BALCONISTA</span>
              <select
                className="gr-input"
                value={pessoa}
                onChange={(e) => setPessoa(e.target.value)}
              >
                <option value="">Todos</option>
                {pessoas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="gr-rotulo">DIA DO GIRO</span>
              <select
                className="gr-input"
                value={diaSel}
                onChange={(e) => setDiaSel(e.target.value)}
              >
                <option value="">Todos do período</option>
                {[...new Set((dados?.itens ?? []).map((i) => i.dia))].map((d) => (
                  <option key={d} value={d}>
                    {diaCurto(d)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="gr-rotulo">SITUAÇÃO</span>
              <select
                className="gr-input"
                value={situacao}
                onChange={(e) => setSituacao(e.target.value as typeof situacao)}
              >
                <option value="">Tudo</option>
                <option value="ERROS">Erros + em análise</option>
                {(Object.keys(SITUACAO) as SituacaoNoRelatorio[]).map((k) => (
                  <option key={k} value={k}>
                    {SITUACAO[k].rotulo}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="gr-rotulo">SETOR</span>
              <select className="gr-input" value={setor} onChange={(e) => setSetor(e.target.value)}>
                <option value="">Todos</option>
                {setores.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="gr-capac-motivo">
              <span className="gr-rotulo">PRODUTO OU CÓDIGO</span>
              <input
                className="gr-input"
                value={busca}
                placeholder="Buscar…"
                onChange={(e) => setBusca(e.target.value)}
              />
            </label>
          </div>
          <p className="cv-cinza cv-peq">
            Giro de {diaCurto(per.de)}
            {per.ate !== per.de ? ` a ${diaCurto(per.ate)}` : ''} · domingo não tem Giro · bloqueia
            com {pct(limitePct)} ou mais
          </p>
        </div>

        {erro ? (
          <ErroDeArea oQue="o relatório do Giro" aoTentar={carregar} />
        ) : !dados ? (
          <div className="cv-carregando" aria-busy="true" />
        ) : (
          <>
            <div className="cv-kpis cv-kpis-5">
              <Kpi
                rotulo="% DE ERRO"
                valor={pct(taxa)}
                sub={tot.pendentes > 0 ? `pode chegar a ${pct(taxaMax)}` : undefined}
                ajuda="Erros confirmados ÷ itens mandados repor, no filtro (balconista e dia). É a mesma conta que bloqueia."
              />
              <Kpi
                rotulo="ERROS CONFIRMADOS"
                valor={String(tot.falhas)}
                sub={`de ${String(tot.tarefas)} mandados repor`}
                ajuda="Disse que não tinha para repor e o Igor/Felipe achou no estoque."
              />
              <Kpi
                rotulo="EM ANÁLISE"
                valor={String(tot.pendentes)}
                sub="ainda não contam"
                ajuda="Casos que o Igor/Felipe ainda não fecharam. Enquanto esperam, não pesam contra ninguém."
              />
              <Kpi
                rotulo="UNIDADES REPOSTAS"
                valor={`${String(unid.repos)} de ${String(unid.pediu)}`}
                ajuda="Soma das unidades repostas ÷ pedidas, nos produtos da lista abaixo (com os filtros)."
              />
              <Kpi
                rotulo="BLOQUEIOS"
                valor={String(bloqueios.length)}
                ajuda="Bloqueios de venda causados pelos Giros do período."
              />
            </div>

            <h2 className="cv-sub">Por balconista e dia</h2>
            <div className="cv-cartao cv-sem-pad">
              {resumo.length === 0 ? (
                <p className="cv-vazio">Nenhum Giro neste filtro.</p>
              ) : (
                <div className="cv-rolagem">
                  <table className="cv-tabela">
                    <thead>
                      <tr>
                        <th>Giro</th>
                        <th>Balconista</th>
                        <th className="cv-dir">Contou</th>
                        <th className="cv-dir">Mandados repor</th>
                        <th className="cv-dir">Erros</th>
                        <th className="cv-dir">Em análise</th>
                        <th className="cv-dir">% agora</th>
                        <th className="cv-dir">% se tudo confirmar</th>
                        <th>Bloqueio</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {resumo.map((l) => {
                        const r = l.d.avaliacao.reposicao;
                        return (
                          <tr key={`${l.accountId}-${l.d.dia}`}>
                            <td>{diaCurto(l.d.dia)}</td>
                            <td>
                              <b>{l.nome}</b>
                            </td>
                            <td className="cv-dir">{l.d.conta.contados}</td>
                            <td className="cv-dir">{l.d.conta.tarefasReposicao}</td>
                            <td className="cv-dir">{l.d.conta.falhasReposicao}</td>
                            <td className="cv-dir">{l.d.conta.pendentesReposicao}</td>
                            <td className="cv-dir">
                              <span className={r.taxa >= limitePct ? 'gr-st gr-st-div' : ''}>
                                {pct(r.taxa)}
                              </span>
                            </td>
                            <td className="cv-dir">
                              <span className={r.taxaMaxima >= limitePct ? 'gr-st gr-st-rep' : ''}>
                                {pct(r.taxaMaxima)}
                              </span>
                            </td>
                            <td>
                              {l.bloqueio ? (
                                <SeloDoBloqueio b={l.bloqueio} />
                              ) : (
                                <span className="cv-cinza">—</span>
                              )}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="cv-botao"
                                onClick={() => {
                                  setPessoa(l.accountId);
                                  setDiaSel(l.d.dia);
                                }}
                              >
                                Ver produtos
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <h2 className="cv-sub">
              Igor e Felipe no período
              <Ajuda rotulo="Igor e Felipe">
                Pela data em que cada um fez a ação: produtos que conferiu na prateleira, produtos
                que conferiu no estoque, em quantos achou no estoque o que o balconista disse que
                não tinha, casos que fechou (inclui os que fecham sozinhos quando o estoque tinha) e
                ajustes lançados/confirmados no HOS. Não usa o filtro de balconista.
              </Ajuda>
            </h2>
            <div className="cv-cartao cv-sem-pad">
              {dados.conferencia.length === 0 ? (
                <p className="cv-vazio">Ninguém conferiu nada neste período.</p>
              ) : (
                <div className="cv-rolagem">
                  <table className="cv-tabela">
                    <thead>
                      <tr>
                        <th>Quem</th>
                        <th className="cv-dir">Conferiu na prateleira</th>
                        <th className="cv-dir">Conferiu no estoque</th>
                        <th className="cv-dir">Achou o que o balconista não achou</th>
                        <th className="cv-dir">Unidades achadas</th>
                        <th className="cv-dir">Casos fechados</th>
                        <th className="cv-dir">Ajustes lançados</th>
                        <th className="cv-dir">Ajustes confirmados no HOS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.conferencia.map((c) => (
                        <tr key={c.accountId}>
                          <td>
                            <b>{c.nome}</b>
                          </td>
                          <td className="cv-dir">{c.prateleira}</td>
                          <td className="cv-dir">{c.estoque}</td>
                          <td className="cv-dir">{c.achouNoEstoque}</td>
                          <td className="cv-dir">{c.unidadesAchadas}</td>
                          <td className="cv-dir">{c.casosFechados}</td>
                          <td className="cv-dir">{c.ajustesLancados}</td>
                          <td className="cv-dir">{c.ajustesConfirmados}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {bloqueios.length > 0 && (
              <>
                <h2 className="cv-sub">Bloqueios e o motivo</h2>
                <div className="cv-cartao cv-sem-pad">
                  <div className="cv-rolagem">
                    <table className="cv-tabela">
                      <thead>
                        <tr>
                          <th>Balconista</th>
                          <th>Giro</th>
                          <th>Bloqueado em</th>
                          <th>Tipo</th>
                          <th className="cv-dir">Erros / mandados</th>
                          <th>Motivo</th>
                          <th>Situação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bloqueios.map((b) => (
                          <tr key={b.id}>
                            <td>
                              <b>{b.balconista}</b>
                            </td>
                            <td>{b.diaGiro ? diaCurto(b.diaGiro) : '—'}</td>
                            <td>{diaCurto(b.dia)}</td>
                            <td>{ORIGEM[b.origem] ?? b.origem}</td>
                            <td className="cv-dir">
                              {b.erros !== null && b.mandados !== null
                                ? `${String(b.erros)} / ${String(b.mandados)}${b.taxa !== null ? ` (${pct(b.taxa)})` : ''}`
                                : '—'}
                            </td>
                            <td>
                              {b.porque}
                              {b.motivo && b.motivo !== b.porque && (
                                <span className="cv-cinza cv-peq"> · {b.motivo}</span>
                              )}
                            </td>
                            <td>
                              <SeloDoBloqueio b={b} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            <div className="cv-cartao-topo">
              <h2 className="cv-sub">
                Produto a produto ({itens.length})
                <Ajuda rotulo="produto a produto">
                  Todo produto que foi mandado repor ou que abriu divergência. Prateleira = o que o
                  balconista contou; Pediu repor = o que o sistema pediu; Achou no estoque = o que
                  ele disse que pegou; Repôs = o que colocou na prateleira; Igor recontou = o que o
                  auditor achou.
                </Ajuda>
              </h2>
              <div className="gr-capac-acoes">
                {(pessoa || diaSel || situacao || setor || busca) && (
                  <button
                    type="button"
                    className="cv-botao"
                    onClick={() => {
                      setPessoa('');
                      setDiaSel('');
                      setSituacao('');
                      setSetor('');
                      setBusca('');
                    }}
                  >
                    Limpar filtros
                  </button>
                )}
                <button
                  type="button"
                  className="cv-botao"
                  disabled={itens.length === 0}
                  onClick={exportar}
                >
                  Baixar planilha
                </button>
              </div>
            </div>
            <div className="cv-cartao cv-sem-pad">
              {itens.length === 0 ? (
                <p className="cv-vazio">Nenhum produto neste filtro.</p>
              ) : (
                <div className="cv-rolagem">
                  <table className="cv-tabela">
                    <thead>
                      <tr>
                        <th>Giro</th>
                        <th>Balconista</th>
                        <th>Produto</th>
                        <th className="cv-dir">Prateleira</th>
                        <th className="cv-dir">Pediu repor</th>
                        <th className="cv-dir">Achou no estoque</th>
                        <th className="cv-dir">Repôs</th>
                        <th>Situação</th>
                        <th>Caso</th>
                        <th className="cv-dir">Igor recontou</th>
                        <th>Justificativa</th>
                        <th>Fotos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itens.slice(0, limite).map((i) => (
                        <LinhaDoItem
                          key={`${i.dia}-${i.accountId}-${i.produto}-${i.codigo ?? ''}`}
                          i={i}
                          abrir={setFoto}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {itens.length > limite && (
              <button type="button" className="cv-botao" onClick={() => setLimite((x) => x + 300)}>
                Mostrar mais ({itens.length - limite} restantes)
              </button>
            )}
          </>
        )}
      </div>

      {foto && (
        <div className="cv-veu gr-fotos-fundo" onClick={() => setFoto(null)}>
          <figure
            className="gr-fotos-grande"
            role="dialog"
            aria-label={foto.titulo}
            onClick={(e) => e.stopPropagation()}
          >
            <img src={`/api/evidencias/${foto.id}`} alt={foto.titulo} />
            <figcaption>
              <b>{foto.titulo}</b>
              <button type="button" className="cv-botao" onClick={() => setFoto(null)}>
                Fechar
              </button>
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}

function SeloDoBloqueio({ b }: { b: BloqueioDoRelatorio }) {
  if (b.liberadoEm)
    return (
      <span className="gr-st gr-st-ok" title={b.liberadoMotivo ?? undefined}>
        Liberado{b.liberadoMotivo ? `: ${b.liberadoMotivo}` : ''}
      </span>
    );
  return <span className="gr-st gr-st-div">Bloqueado {diaCurto(b.dia)}</span>;
}

function LinhaDoItem({
  i,
  abrir,
}: {
  i: ItemDoRelatorio;
  abrir: (f: { id: string; titulo: string }) => void;
}) {
  const s = SITUACAO[i.situacao];
  const d = i.divergencia;
  return (
    <tr>
      <td>{diaCurto(i.dia)}</td>
      <td>{i.balconista}</td>
      <td>
        <b>{i.produto}</b>
        <span className="cv-cinza cv-peq">
          {' '}
          · {i.setor}
          {i.codigo ? ` · ${i.codigo}` : ''}
        </span>
      </td>
      <td className="cv-dir">{n(i.prateleira)}</td>
      <td className="cv-dir">{n(i.repor)}</td>
      <td className="cv-dir">{n(i.encontrado)}</td>
      <td className="cv-dir">{n(i.reposto)}</td>
      <td>
        <span className={`gr-st ${s.classe}`} title={s.ajuda}>
          {s.rotulo}
        </span>
      </td>
      <td>
        {d ? (
          <>
            {TIPO[d.tipo] ?? d.tipo}
            <span className="cv-cinza cv-peq">
              {' '}
              · {d.estado === 'CONCLUIDA' ? 'fechado' : (ETAPA[d.etapa] ?? d.etapa)}
              {d.numero !== null ? ` · nº ${String(d.numero)}` : ''}
            </span>
          </>
        ) : (
          <span className="cv-cinza">—</span>
        )}
      </td>
      <td className="cv-dir">{d ? n(d.recontagem) : '—'}</td>
      <td>
        {d?.observacao ? (
          <>
            {d.observacao}
            {d.auditor && <span className="cv-cinza cv-peq"> · {d.auditor}</span>}
          </>
        ) : (
          <span className="cv-cinza">—</span>
        )}
      </td>
      <td>
        <div className="gr-capac-acoes">
          {i.fotoPrateleira && (
            <button
              type="button"
              className="cv-botao"
              onClick={() =>
                abrir({ id: i.fotoPrateleira ?? '', titulo: `Prateleira · ${i.produto}` })
              }
            >
              Prateleira
            </button>
          )}
          {i.fotoCesta ? (
            <button
              type="button"
              className="cv-botao"
              onClick={() =>
                abrir({
                  id: i.fotoCesta ?? '',
                  titulo: `Cesta de ${i.balconista} · ${diaCurto(i.dia)}`,
                })
              }
            >
              Cesta
            </button>
          ) : (
            (i.reposto ?? 0) > 0 && <span className="cv-cinza cv-peq">sem foto da cesta</span>
          )}
        </div>
      </td>
    </tr>
  );
}

function Kpi({
  rotulo,
  valor,
  sub,
  ajuda,
}: {
  rotulo: string;
  valor: string;
  sub?: string | undefined;
  ajuda: string;
}) {
  return (
    <div className="cv-kpi">
      <div className="cv-kpi-rotulo">
        {rotulo}
        <Ajuda rotulo={rotulo.toLowerCase()}>{ajuda}</Ajuda>
      </div>
      <div className="cv-kpi-valor">{valor}</div>
      {sub && <div className="cv-kpi-sub">{sub}</div>}
    </div>
  );
}
